/**
 * **The hand-over from the shelf to the till** (`docs/lovespa-gaps-plan.md`
 * C1 → B4) — one reservation id, staged in memory, taken exactly once.
 *
 * ## Why this exists at all
 *
 * Collecting a reservation and *ringing it up* are two different facts and they
 * are recorded by two different services: `POST /v1/reservations/{id}/status`
 * says the jar left the shelf, and `POST /v1/settlements` says what was charged
 * for it (§6.5's document, with its own tender waterfall and its own audit
 * row). The Reservations screen owns the first and must not own the second — a
 * second implementation of "what was charged" is precisely what §7.4 rule 3
 * exists to prevent.
 *
 * So the screens are joined by a *hand-off* rather than by a shared component:
 * Reservations stages an id here, and Checkout takes it.
 *
 * ## Why a module and not a URL
 *
 * `apps/app` has no router — `App.tsx` holds a `Tab` in state and renders one
 * screen — so there is no address to link to. A `href="#/checkout?..."` would be
 * a link that looks like it works and does nothing, which is worse than a
 * sentence telling somebody to press Checkout.
 *
 * ## Why it is taken once
 *
 * A staged id is an *intention*, not a state: once the till has read it, the
 * cashier is looking at the lines and the intention is spent. Leaving it set
 * would re-open the same reservation the next time anybody opened the till,
 * which is how a shop double-rings a sale.
 *
 * ## Who takes it, and why it is not `CheckoutSheet`'s own mount
 *
 * **The host takes it in the click handler that opens the sheet** — today
 * `Today.tsx`'s till button — and passes it in as `CheckoutPrefill`'s
 * `reservationId`.
 *
 * The obvious alternative, a take inside `CheckoutSheet` on mount, is wrong for
 * one concrete reason: `main.tsx` renders the app inside `StrictMode`, which
 * double-invokes render bodies and remounts effects in development. A take that
 * ran twice would hand the first pass the reservation and the second one `null`,
 * and the bug would appear only on a developer's laptop. An event handler runs
 * exactly once, in every mode.
 *
 * ## What the till does with it
 *
 * ```ts
 * // GET /v1/reservations/{staged}/lines → { lines: [{ item_id, name, qty,
 * //   unit_price_cents }], client_id, status, outstanding_cents }
 * ```
 *
 * Those lines are the settlement's **draft**: the till prices its own document
 * from the catalog (B7 at the counter), and the quoted `unit_price_cents` is
 * what the guest was told on the website — a counter that rings up a different
 * number because the catalog moved in between has an argument on its hands. When
 * the two differ the gap lands where every other gap does, as §7.4 rule 3's
 * signed `list − charged`.
 *
 * The settlement is queued first and, when the shelf does not already say
 * `collected`, `reservation.status` is queued behind it — one tap, two intents,
 * in the order the ledger needs them.
 */

let staged: string | null = null;

/** Reservations calls this when somebody marks one collected. */
export function stageReservationForTill(reservationId: string): void {
  staged = reservationId;
}

/** Checkout calls this on mount. Returns `null` when nothing is waiting. */
export function takeStagedReservation(): string | null {
  const value = staged;
  staged = null;
  return value;
}

/** Whether something is waiting, without consuming it — for a hint on a screen. */
export function hasStagedReservation(): boolean {
  return staged !== null;
}
